import { useCallback, useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { fetchNews, catName } from "@/lib/api";
import { useNow } from "@/hooks/useNow";
import { NewsCard, NewsRow } from "@/components/news/NewsCard";
import { SectionTitle } from "@/components/news/CategorySection";
import { AdSlot } from "@/components/news/Ads";
import { GridSkeleton } from "@/components/news/Skeletons";

export default function CategoryPage() {
  const { slug } = useParams();
  const loc = useLocation();
  const isSource = loc.pathname.startsWith("/fonte/");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  useNow();
  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const d = await fetchNews({ [isSource ? "source" : "category"]: slug, page: p, limit: 24 });
      setItems((prev) => (p === 1 ? d.items : [...prev, ...d.items]));
      setHasMore(d.has_more); setPage(p);
    } finally { setLoading(false); }
  }, [slug, isSource]);
  useEffect(() => { setItems([]); load(1); }, [load]);
  const name = isSource ? loc.state?.name || slug : catName(slug);
  return (
    <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
      <Helmet>
        <title>{`${name} | Últimas notícias de Moçambique | Notícia 365`}</title>
        <meta name="description" content={`Últimas notícias de ${name} em Moçambique, atualizadas ao minuto.`} />
        <link rel="canonical" href={`${window.location.origin}${loc.pathname}`} />
      </Helmet>
      <h1 data-testid="category-title" className="font-serif-display text-4xl font-black uppercase text-primary sm:text-5xl">{name}</h1>
      <p className="mt-2 text-muted-foreground">Últimas notícias de {name.toLowerCase()} em Moçambique, ao minuto.</p>
      <div className="mt-8 grid gap-12 lg:grid-cols-12">
        <section className="lg:col-span-8">
          {loading && !items.length ? <GridSkeleton /> : (
            <div data-testid="category-grid" className="grid gap-x-6 gap-y-10 sm:grid-cols-2">{items.map((a, i) => <NewsCard key={a.id} a={a} i={i} />)}</div>
          )}
          {!loading && !items.length && <p data-testid="category-empty" className="text-muted-foreground">Sem notícias nesta categoria por agora.</p>}
          {hasMore && (
            <button data-testid="load-more-btn" disabled={loading} onClick={() => load(page + 1)} className="mx-auto mt-12 block rounded-full border-2 border-foreground px-8 py-3 text-sm font-bold hover:bg-foreground hover:text-background disabled:opacity-50">
              {loading ? "A carregar…" : "Carregar mais notícias"}
            </button>
          )}
        </section>
        <aside className="space-y-8 lg:col-span-4">
          <div className="lg:sticky lg:top-32 space-y-8">
            <AdSlot slot="sidebar" />
            <div><SectionTitle>Mais recentes</SectionTitle><div className="divide-y">{items.slice(0, 5).map((a) => <NewsRow key={a.id} a={a} />)}</div></div>
          </div>
        </aside>
      </div>
    </div>
  );
}
